import React, { useState, useEffect, forwardRef } from 'react';
import { Input, Button, Toast } from 'zarm';
import classname from 'classnames';
import { AntiBrush } from '@iyunbao/components';
import debounce from 'lodash.debounce';

import UItem from '../item';

import { clearObj, getFormId } from '../../utils/tools';
import { BaseWidgetProps, FetchProps } from '../../utils/type';
import { ButtonProps } from '../button';
import util from './_util';

export interface SmsCodeProps extends BaseWidgetProps {
  placeholder?: string;
  defaultValue?: any;
  defaultSend?: boolean;
  hiddenInput?: boolean;
  sendByBackend?: boolean;
  btnDesc?: string;
  action?: any;
  onSendBackendCB?: (action: any, param: any) => void;
  value?: any;
  readonly?: boolean;
  readOnly?: boolean;
  label?: string;
  SMSCD?: number;
  maxLength?: number;
  disabled?: boolean;
  data?: string;
  sourceName?: string;
  factorsValue?: boolean;
  extendName?: string[];
  refreshPrice?: boolean;
  fetch?: FetchProps;
  fetchPhoneName?: string;
  btnProps: ButtonProps,
  configs?: {
    bizType: string;
    sId?: string;
  };
  className?: string;
  onSendCB?: (props: any, content: any) => void;
}

// 发送验证码组件
export const SmsCodeItem = forwardRef((props: SmsCodeProps, ref: any) => {
  const {
    data,
    fetch,
    configs,
    prefixCls,
    SMSCD = 60,
    maxLength = 6,
    className,
    sourceName, // 用于获取数据于验证数据的key @todo
    // 请求参数手机key
    fetchPhoneName,
    // 附加因子
    factorsValue,
    // 扩展因子
    extendName,
    _crengine,
    value,
    btnProps,
    onSendCB,
    defaultSend,
    hiddenInput,
    sendByBackend,
    action,
    onSendBackendCB,
    btnDesc,
    ...rest
  } = props;
  const { path, form = {} } = _crengine || {};

  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const [phone, setPhone] = useState(data);
  const [extendParams, setExtendParams] = useState({});

  // 第三方保险公司，发送验证码。已签约不需要输入验证码
  const [hideComonpent, setHideCommonpent] = useState(false);

  const cls = classname(prefixCls, className);

  const configsAnti = {
    bizType: 'insure_verify',
    sId: 'iyunbao_h5#insure_verify',
    ...configs,
  };

  useEffect(() => {
    setPhone(data);
  }, [data]);

  // 用于倒计时
  useEffect(() => {
    let timeout: any = null;
    if (count > 0) {
      timeout = setTimeout(() => {
        setCount((tempCount) => tempCount - 1);
      }, 1000);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [count]);

  // 按钮的样式
  const btnCls = classname(`${prefixCls}-button`, {
    [`${prefixCls}-button-disabled`]: count,
  });

  // 发送验证码
  const sendSmsCode = () => {
    if (count !== 0) {
      return;
    }

    if (form) {
      const { validateFields, getFieldValue } = form;

      const names = getFormId(sourceName, path);
      const nameErrors = [names];

      // 扩展因子
      if (extendName && Array.isArray(extendName) && extendName.length) {
        extendName.map((key: string) => nameErrors.push(getFormId(key, path)));
      }

      validateFields && validateFields(nameErrors, {}, (errors: any, values: any) => {
        if (errors) return;

        let tempPhone = getFieldValue(names);

        // 掩码需求，如果手机号是掩码，通过data传值
        if (
          typeof tempPhone === 'string'
          && tempPhone.includes('*')
          && data
          && /^1\d{10}$/.test(data)
        ) {
          tempPhone = data;
        }

        // 发送验证码
        setPhone(tempPhone);
        setVisible(true);
        setExtendParams(values);
      });
    } else if (phone && /^1\d{10}$/.test(phone)) {
      setVisible(true);

      Toast.show({
        content: '手机号码格式不正确',
      });
    }
  };


  useEffect(() => {
    if (defaultSend) {
      sendSmsCode();
    }
  }, [defaultSend]);

  if (hideComonpent) {
    return null;
  }

  return (
    <div className={cls}>
      {!hiddenInput && (
        <Input
          type="tel"
          ref={ref}
          className={`${prefixCls}-input`}
          maxLength={maxLength}
          clearable={false}
          value={value}
          {...rest}
        />
      )}

      <Button
        className={btnCls}
        size="xs"
        shape="round"
        {...btnProps}
        disabled={visible}
        onClick={debounce(() => {
          sendSmsCode();
        }, 200)}
      >
        {count ? `${count}秒` : btnDesc || '发送验证码'}
      </Button>

      <AntiBrush
        {...configsAnti}
        visible={visible}
        data={phone}
        onSuccess={(ticket: string) => {
          setVisible(false);
          if (sendByBackend && onSendBackendCB) {
            onSendBackendCB(action, {ticket});
            setCount(SMSCD);
            return;
          }
          const params: any = {
            ticket,
            mobile: phone,
            fetch,
            // 配置手机号码的别名key
            fetchPhoneName,
          };

          // 附加因子，签约可用
          if (factorsValue && form) {
            params.factors = form.getFieldsValue();
          }

          // 附加参数
          if (extendName && Array.isArray(extendName) && extendName.length) {
            params.content = extendParams;
          }

          // 发送验证码
          util.sendSMS({
            ...params,
            success(content) {
              const { sourceResult } = content || {};
              // 发送验证成功回调
              onSendCB && onSendCB(props, sourceResult || content);
              setCount(SMSCD);
            },
            fail(content) {
              // 第三方保险公司，发送验证码。已签约不需要输入验证码
              if ((+content.code && +content.code === 601) || (content.result && content.result.code && +content.result.code === 601)) {
                setHideCommonpent(true);
              }
            },
          });
        }}
        onClose={() => {
          setVisible(false);
        }}
      />
    </div>
  );
});

// 表单 输入框组件
function SmsCode(props: SmsCodeProps) {
  const {
    value,
    defaultValue,
    defaultSend,
    hiddenInput,
    sendByBackend, // 是否通过后端发送验证码， 需要将ticket传后端
    action, // 通过后端发送验证码时的对应配置
    onSendBackendCB, // 获取ticket后的回调
    btnDesc, // 按钮文案描述，默认为“发送验证码”
    placeholder,
    className,
    readOnly,
    readonly,
    data,
    SMSCD,
    maxLength,
    disabled,
    sourceName = 'mobile',
    fetchPhoneName,
    factorsValue,
    extendName,
    prefixCls = 'crm-smscode',
    configs,
    fetch,
    onChange,
    onSendCB,
    btnProps,
    _crengine,
    ...rest
  } = props;

  // 子项内容改变的时候进行数据更新
  const onValueChange = (e: any) => {
    const { onChange: onChangeV, ...otherProps } = props;
    if (!onChange) return;
    onChange({
      ...otherProps,
      value: e,
    });
  };

  const smsCodeProps = clearObj({
    value,
    defaultValue,
    defaultSend,
    hiddenInput,
    sendByBackend,
    action,
    onSendBackendCB,
    btnDesc,
    placeholder,
    _crengine,
    prefixCls,
    disabled,
    data,
    fetch,
    configs,
    SMSCD,
    maxLength,
    className,
    sourceName, // 用于获取数据于验证数据的key @todo
    fetchPhoneName,
    factorsValue,
    extendName,
    readOnly: readOnly || readonly,
    btnProps,
    onBlur: onValueChange,
    onSendCB,
  });

  const { form } = _crengine || {};

  // 如果wform存在 则去除input组件的某些属性
  if (form) {
    delete smsCodeProps.value;
    delete smsCodeProps.defaultValue;
    delete smsCodeProps.onBlur;
  }

  return (
    <UItem
      {...rest}
      itemType="smscode"
      _crengine={_crengine}
      initialValue={value || defaultValue}
      validateTrigger="onBlur"
      onBlur={onValueChange}
    >
      <SmsCodeItem {...smsCodeProps} />
    </UItem>
  );
}

export default SmsCode;
